// TODO - Type 'any' needs to be fixed.
/* eslint-disable simple-import-sort/imports */
import { Alert, Box, Button, Snackbar, Tooltip, Typography } from "@mui/material";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SkillRepresentationCell } from "../../../../../../export/cross-skilling/index";
import { getLevelObj, hasPermission } from "../../../../../utils/helpers";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { deleteWorkerSkills, openEditWorkerSkillDialog } from "../../../../../../export/workers";

import { useDispatch, useSelector } from "react-redux";
import { useConfirm } from "material-ui-confirm";
import ApproveSkillUpgradeDialog from "../../../../cross-skilling/ui/cross-skill-table/ui/approve-skill-upgrade-dialog";
import { SkillLevel } from "../../../../workers/types";
import { addSkillInterested, fetchWorkerProfile } from "../../../../workers/store/manage-workers-slice";
import { format, isFuture } from "date-fns";
import Highlight from "../../../../Highlight";
import JobColumn from "../ui/table/job-column";

interface GridColumn {
  field: string;
  headerName?: string;
  flex?: number;
  width?: number;
  minWidth?: number | string;
  renderHeader?: (params: any) => JSX.Element;
  renderCell?: (params: any) => JSX.Element;
  sortable?: boolean;
  disableColumnMenu?: boolean;
  cellClassName?: string;
  headerClassName?: string;
  valueGetter?: (params: any) => any;
  filterable?: boolean;
}

const deleteContent = ({ message }: { message: string }) => (
  <Box>
    <Typography
      sx={{
        fontFamily: "Roboto",
        fontSize: "16px",
        fontWeight: 400,
        lineHeight: "28px",
      }}
    >
      {message}
    </Typography>
  </Box>
);

const SkillValue: React.FC<{ value: number }> = ({ value }) => {
  const skillLevels = useSelector(
    (state: any) => state.manageWorkers.skillLevels
  );
  if (value >= 0) {
    const { levelValue, repLevel, backgroundColor, foregroundColor, repType } =
      getLevelObj(String(value), skillLevels);
    return (
      <SkillRepresentationCell
        level={value}
        value={levelValue}
        representationType={repType}
        representationLevel={repLevel}
        backgroundColor={backgroundColor}
        foregroundColor={foregroundColor}
        border={1}
        height={20}
        width={20}
        backgroundWidth={"30px"}
        backgroundHeight={"30px"}
      />
    );
  } else {
    return <></>;
  }
};

const InterestedSkillActionCell: React.FC<{
  params: any;
  isInUserWorkcenter: boolean;
  handleisInUserWorkcenterAlert: () => void;
  workerId: number | string | null;
  workerFirstName: string;
  allSkillLevels: SkillLevel[];
}> = ({
  params,
  isInUserWorkcenter,
  handleisInUserWorkcenterAlert,
  workerId,
  workerFirstName,
  allSkillLevels,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const targetSkillLevelId = params.row.targetSkillLevelId;
  const requestedLevel =
    allSkillLevels.find((l) => l.id === targetSkillLevelId) ||
    allSkillLevels[0];

  const handleConfirm = useCallback(
    (selectedLevelId: number, comment: string) => {
      const interestedSkillId = params.row.interestedSkillId;
      if (!interestedSkillId) return;
      setLoading(true);
      dispatch(
        addSkillInterested({
          id: String(interestedSkillId),
          overrideLevelId: selectedLevelId,
          comment: comment || undefined,
        }) as any
      )
        .then((result: any) => {
          if (result?.error) {
            setSnackbar({
              open: true,
              message: result.payload || t("workers_skill-upgrade-failed"),
              severity: "error",
            });
            setLoading(false);
          } else {
            setSnackbar({
              open: true,
              message: t("workers_skill-upgrade-success"),
              severity: "success",
            });
            if (workerId) {
              dispatch(fetchWorkerProfile(String(workerId)) as any);
            }
            setTimeout(() => {
              setDialogOpen(false);
              setLoading(false);
            }, 500);
          }
        })
        .catch(() => {
          setSnackbar({
            open: true,
            message: t("workers_skill-upgrade-failed"),
            severity: "error",
          });
          setLoading(false);
        });
    },
    [dispatch, params.row.interestedSkillId, workerId, t]
  );

  return (
    <>
      <Box
        className="hover-content"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: "100px",
          padding: "12px 16px 8px 16px",
          backgroundColor: "#FFF",
          borderRadius: "4px",
        }}
      >
        <Box
          sx={{
            color: "#6c6c6c",
            "&:hover": {
              color: "#000000",
              transform: "scale(1.2)",
            },
            cursor: "pointer",
          }}
        >
          <Tooltip title={t("common_actions")}>
            <FactCheckOutlinedIcon
              fontSize="small"
              onClick={() => {
                if (isInUserWorkcenter) {
                  setDialogOpen(true);
                } else {
                  handleisInUserWorkcenterAlert();
                }
              }}
            />
          </Tooltip>
        </Box>
      </Box>
      {requestedLevel && (
        <ApproveSkillUpgradeDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onConfirm={handleConfirm}
          workerFirstName={workerFirstName}
          skillName={params.row.skillName}
          requestedLevel={requestedLevel}
          defaultSelectedLevelId={targetSkillLevelId ?? requestedLevel.id}
          allSkillLevels={allSkillLevels}
        />
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

const useWorkerSkillColumn = (searchText: string, isRestricted = false) => {
  const confirm = useConfirm();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const workerProfile = useSelector(
    (state: any) => state.manageWorkers.workerProfile.profile
  );

  const userRolesWithPermission = useSelector(
    (state: any) => state.user.userData.userRolesWithPermissions
  );

  const { userWorkCenters } = useSelector((state: any) => ({
    userWorkCenters:
      state.manageWorkers.filterData.allWorkcenters?.map((wc: any) => String(wc.id)) ||
      [],
  }));

  const isInUserWorkcenter = useMemo(() => {
    const workerWorkCenterId = workerProfile?.workCenter?.id;
    if (!workerWorkCenterId) return true;
    return userWorkCenters.includes(String(workerWorkCenterId));
  }, [workerProfile?.workCenter?.id, userWorkCenters]);

  const handleisInUserWorkcenterAlert = useCallback(() => {
    confirm({
      title: "",
      description: t("workers_no-workcenter-access-skills"),
      confirmationText: t("common_close"),
      cancellationText: "",
    });
  }, [confirm, t]);

  const workerId = workerProfile?.workerId || null;
  const workerFirstName = workerProfile?.firstName || "";

  const skillLevelsForDialog: SkillLevel[] =
    useSelector((state: any) => state.manageWorkers.skillLevels) || [];

  const columns: GridColumn[] = [
    {
      field: "SkillName",
      headerName: t("workers_skill-name"),
      minWidth: 185,
      flex: 1,
      renderHeader: () => {
        return (
          <Box>
            <Typography
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: "14px",
                color: "#000000",
                letterSpacing: "0.17px",
              }}
            >
              {t("workers_skill-name")}
            </Typography>
          </Box>
        );
      },
      renderCell: (params: any) => {
        return (
          <Tooltip title={params.row.skillName} disableInteractive>
            <Box mt={1} mb={1} sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", width: "100%" }}>
              <Highlight searchText={searchText} value={params.row.skillName} />
            </Box>
          </Tooltip>
        );
      },
      valueGetter: (params: any) => params.row.skillName,
    },
    {
      field: "skillPriority",
      headerName: t("workers_skill-priority"),
      minWidth: 185,
      flex: 1,
      renderHeader: () => {
        return (
          <Box>
            <Typography
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: "14px",
                color: "#000000",
                letterSpacing: "0.17px",
              }}
            >
              {t("workers_skill-priority")}
            </Typography>
          </Box>
        );
      },
      renderCell: (params: any) => {
        if (params.row.skillPriority) {
          return (
            <Box mt={1} mb={1}>
              <Highlight
                searchText={searchText}
                value={params.row.skillPriority}
              />
            </Box>
          );
        } else return <></>;
      },
      valueGetter: (params: any) => params.row.skillPriority,
    },
    {
      field: "status",
      minWidth: 120,
      flex: 1,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: (params: any) => {
        return (
          <Box>
            <Typography
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: "14px",
                color: "#000000",
                letterSpacing: "0.17px",
              }}
            >
              {t("workers_skill-status")}
            </Typography>
          </Box>
        );
      },
      renderCell: (params: any) => {
        // console.log(params.row);
        const validFrom = params.row.validFrom;
        const validUntil = params.row.validTo;

        if (!validFrom || isFuture(new Date(validFrom))) return <></>;
        if (!validUntil || isFuture(new Date(validUntil)))
          return (
            <Box>
              <Highlight searchText={searchText} value={t("workers_active")} />
            </Box>
          );

        return (
          <Box>
            <Highlight
              searchText={searchText}
              value={t("workers_expired")}
              color="#E44B48"
            />
          </Box>
        );
      },
      valueGetter: (params: any) => {
        const validFrom = params.row.validFrom;
        const validUntil = params.row.validTo;

        if (!validFrom || isFuture(new Date(validFrom))) return "Active";
        if (!validUntil || isFuture(new Date(validUntil))) return "Expired";

        return "Expired";
      },
    },
    {
      field: "skillLevel",
      minWidth: 170,
      flex: 1,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: (params: any) => {
        return (
          <Box>
            <Typography
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: "14px",
                color: "#000000",
                letterSpacing: "0.17px",
              }}
            >
              {t("workers_skill-level")}
            </Typography>
          </Box>
        );
      },
      renderCell: (params: any) => {
        const isPending = params.row?.isPending;
        const isInterested = params.row?.isInterested;
        if (isInterested)
          return (
            <Box display="flex" alignItems="center">
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontSize: "14px",
                  fontWeight: 400,
                  lineHeight: "28px",
                  color: "#49454F",
                }}
              >
                {t("workers_interested")}
              </Typography>
            </Box>
          );
        if (isPending)
          return (
            <Box display="flex" alignItems="center">
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontSize: "14px",
                  fontWeight: 400,
                  lineHeight: "28px",
                }}
              >
                {t("workers_evaluation-requested")}
              </Typography>
            </Box>
          );
        const level = params.row?.level ?? 0;

        return (
          <Box display="flex" alignItems="center">
            <SkillValue value={level} />
            <Box ml={1}>
              <Highlight
                searchText={searchText}
                value={params.row?.levelName ?? ""}
              />
            </Box>
          </Box>
        );
      },
      valueGetter: (params: any) => params.row.level,
    },
    {
      field: "associatedJobs",
      minWidth: 310,
      flex: 1,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: (params: any) => {
        return (
          <Box>
            <Typography
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: "14px",
                color: "#000000",
                letterSpacing: "0.17px",
              }}
            >
              {t("workers_worker-job-roles")}
            </Typography>
          </Box>
        );
      },
      renderCell: (params: any) => {
        const jobs = params.row?.associatedJobs ?? [];
        // const { jobString, remainig, seeMore } = generateJobString(jobs);
        // const jobsText = jobString + " " + seeMore;

        if (jobs.length === 0) return <Box>-</Box>;
        return (
          <Box maxWidth={260} display="flex">
            <JobColumn jobs={jobs} searchText={searchText} />
          </Box>
        );
      },
      valueGetter: (params: any) => params.row?.associatedJobs?.length,
    },
    {
      field: "validFrom",
      minWidth: 130,
      flex: 1,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: (params: any) => {
        return (
          <Box>
            <Typography
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: "14px",
                color: "#000000",
                letterSpacing: "0.17px",
              }}
            >
              {t("workers_valid-from")}
            </Typography>
          </Box>
        );
      },
      renderCell: (params: any) => {
        if (!params.row.validFrom) return <>-</>;

        const datetime = params.row.validFrom + "T00:00:00";
        const validFromDate = format(new Date(datetime), "MM/dd/yyyy");

        return (
          <Box>
            <Highlight searchText={searchText} value={validFromDate} />
          </Box>
        );
      },
      valueGetter: (params: any) => params.row.validFrom,
    },
    {
      field: "validUntil",
      minWidth: 130,
      flex: 1,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: (params: any) => {
        return (
          <Box>
            <Typography
              sx={{
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: "14px",
                color: "#000000",
                letterSpacing: "0.17px",
              }}
            >
              {t("workers_valid-until")}
            </Typography>
          </Box>
        );
      },
      renderCell: (params: any) => {
        if (!params.row.validTo) return <>-</>;
        const datetime = params.row.validTo + "T00:00:00";
        const validToDate = format(new Date(datetime), "MM/dd/yyyy");

        return (
          <Box>
            <Highlight searchText={searchText} value={validToDate} />
          </Box>
        );
      },
      valueGetter: (params: any) => params.row.validTo,
    },
  ];

  if (
    !isRestricted &&
    hasPermission(userRolesWithPermission, "SETTINGS.EDIT_WORKERS")
  ) {
    columns.push({
      field: "actions",
      width: 80,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: (params: any) => <></>,
      cellClassName: "hover-column-cell",
      //headerClassName: "hover-column-header",
      renderCell: (params: any) => {
        const isPending = params.row.isPending;
        const isInterested = params.row.isInterested;

        const handleDelete = () => {
          if (!params.row.skillId || !workerId) return;
          confirm({
            title: t("workers_remove-skill-title"),
            content: deleteContent({
              message: t("workers_remove-skill-content", {
                skillName: params.row.skillName,
              }),
            }),
            dialogProps: {
              maxWidth: false,
              fullWidth: false,
            },
            contentProps: {
              sx: {
                width: "850px",
              },
            },
            cancellationText: t("common_cancel"),
            confirmationText: (
              <Button variant="contained" color="error">
                {t("workers_remove")}
              </Button>
            ),
          })
            .then(() =>
              dispatch(
                deleteWorkerSkills({
                  workerId: workerId,
                  skillId: params.row.skillId,
                })
              )
            )
            .catch(() => console.log("Deletion cancelled."));
        };

        if (isPending) return <></>;

        if (isInterested) {
          return (
            <InterestedSkillActionCell
              params={params}
              isInUserWorkcenter={isInUserWorkcenter}
              handleisInUserWorkcenterAlert={handleisInUserWorkcenterAlert}
              workerId={workerId}
              workerFirstName={workerFirstName}
              allSkillLevels={skillLevelsForDialog}
            />
          );
        }

        return (
          <Box
            className="hover-content"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "100px",
              padding: "12px 16px 8px 16px",
              backgroundColor: "#FFF",
              borderRadius: "4px",
            }}
          >
            <Box
              sx={{
                color: "#6c6c6c",
                "&:hover": {
                  color: "#000000",
                  transform: "scale(1.2)",
                },
                cursor: "pointer",
              }}
            >
              <Tooltip title={t("common_edit")} disableInteractive>
                <EditIcon
                  id={`edit-job-${params?.row?.id}-enabled`}
                  fontSize="small"
                  onClick={(e) => {
                    if (isInUserWorkcenter) {
                      dispatch(openEditWorkerSkillDialog(params.row));
                    } else {
                      handleisInUserWorkcenterAlert();
                    }
                  }}
                />
              </Tooltip>
            </Box>
            <Box
              sx={{
                color: "#6c6c6c",
                "&:hover": {
                  color: "red",
                  transform: "scale(1.2)",
                },
                cursor: "pointer",
              }}
            >
              <Tooltip title={t("workers_delete")} disableInteractive>
                <DeleteIcon
                  id={`delete-job-${params?.row?.id}-enabled`}
                  sx={{ marginLeft: "8px" }}
                  fontSize="small"
                  onClick={() => {
                    if (isInUserWorkcenter) {
                      handleDelete();
                    } else {
                      handleisInUserWorkcenterAlert();
                    }
                  }}
                />
              </Tooltip>
            </Box>
          </Box>
        );
      },
    });
  }
  return columns;
};

export default useWorkerSkillColumn;
